'use client'

import * as React from 'react'
import type { I18nContextValue } from '@/lib/i18n'
import type { PermissionRequirement } from '@/lib/auth/permissions'
import type { ActivityDto } from '@/lib/activities/types'
import { handled } from '@/lib/activities/types'
import { ER_ACTIVITY_RENDERER } from './activity-dock-er'

type Translate = I18nContextValue['t']

/** One number in a card's grid: what it is called and its value (already formatted for the reader). */
export interface ActivityTile {
  key: string
  label: string
  value: string
  tone?: 'bad'
}

/**
 * How one module's activity is drawn in the dock. The dock owns the chrome (the bar, the drawer, the
 * card, the status badge, the failure reasons, the dismiss button); a renderer owns the WORDS and the
 * module's own actions, because only the module knows what its counters mean and which endpoints
 * pause, retry or undo them. A server adapter can ship before its renderer: an unknown kind is
 * drawn by FALLBACK_RENDERER (counts, no actions), never dropped and never a crash.
 */
export interface ActivityRenderer {
  /** The checks of the module's own endpoints: a role that passes none never has the dock polled for it. */
  requires: readonly PermissionRequirement[]
  /** Query-key prefixes of the module's mutations: invalidating one means work may have started or ended. */
  refreshKeys: readonly (readonly unknown[])[]
  moduleLabel: (t: Translate) => string
  /** The card's title as a sentence (also its accessible name and what is announced). */
  titleText: (a: ActivityDto, t: Translate, n: (v: number) => string) => string
  /** The card's title as drawn: typed text (a run's name, a folder) goes through <UserText>. */
  title: (a: ActivityDto, t: Translate, n: (v: number) => string) => React.ReactNode
  /** The pill's second line while this is the lead activity ("Searching 45 of 100 mailboxes · 1,234 matches"). */
  pillLine: (a: ActivityDto, t: Translate, n: (v: number) => string) => string
  tiles: (a: ActivityDto, t: Translate, n: (v: number) => string) => ActivityTile[]
  /** The words for a failure's reason code. */
  reasonLabel: (t: Translate, code: string) => string
  /** The module's buttons for this activity, gated on the viewer's grants and calling the module's own endpoints. */
  Actions: React.ComponentType<{ activity: ActivityDto }>
}

const NoActions: React.ComponentType<{ activity: ActivityDto }> = () => null

const FALLBACK_RENDERER: ActivityRenderer = {
  requires: [],
  refreshKeys: [],
  moduleLabel: (t) => t('activities.fallback.module'),
  titleText: (a, t) => t('activities.fallback.title'),
  title: (a, t) => t('activities.fallback.title'),
  pillLine: (a, t, n) => t(`activities.card.progress.${a.unit}`, { done: n(handled(a.counts)), total: n(a.counts.total) }),
  tiles: (a, t, n) => [
    { key: 'done', label: t('activities.tile.done'), value: n(a.counts.done) },
    { key: 'remaining', label: t('activities.tile.left'), value: n(a.counts.remaining) },
    { key: 'failed', label: t('activities.tile.failed'), value: n(a.counts.failed), tone: a.counts.failed > 0 ? 'bad' : undefined },
    { key: 'skipped', label: t('activities.tile.skipped'), value: n(a.counts.skipped) },
  ],
  reasonLabel: (t, code) => code,
  Actions: NoActions,
}

/** Every module that has activity in the dock, by the `kind` its server adapter stamps. */
const RENDERERS: Record<string, ActivityRenderer> = {
  'er.snr.search': ER_ACTIVITY_RENDERER,
  'er.snr.action': ER_ACTIVITY_RENDERER,
  'er.snr.org_remediation': ER_ACTIVITY_RENDERER,
}

export function rendererFor(kind: string): ActivityRenderer {
  return RENDERERS[kind] ?? FALLBACK_RENDERER
}

const unique = <T,>(items: T[]): T[] => [...new Set(items)]

/** The checks that let a role see ANY module's activity: the dock is polled only for a role that passes one. */
export const ACTIVITY_REQUIREMENTS: readonly PermissionRequirement[] = unique(
  Object.values(RENDERERS).flatMap((r) => [...r.requires]),
)

/** The query-key prefixes whose invalidation makes the dock ask again. */
export const ACTIVITY_REFRESH_KEYS: readonly (readonly unknown[])[] = unique(
  Object.values(RENDERERS).flatMap((r) => [...r.refreshKeys]),
)
